import { Moment } from 'moment-timezone';
import { PetService } from '@spike/new-booking-model';
import { Product } from '@spike/product-model';
import AddOnService from '@spike/addon-service-model';
import { ValueTypeSelectorValue } from 'components/UI/V3/Forms/ValueTypeSelector';
import { Room } from '@spike/room-model';
import {
    BoardingOffer,
    BoardingServiceWrapper,
    DaycareServiceWrapper,
    LatePickupFee,
    ServiceItem,
    TransportationService,
    SessionBookingType,
    TrainingService,
    WalkingService
} from './Service';
import { TransportationType } from 'components/CreateBooking/Transportation/UI/TransportationTypeSelector/TransportationTypeSelector';
import Staff from './Staff';
import { Vehicle } from '@spike/vehicle-model';
import { DaySchedule } from 'components/CreateBooking/Daycare/FeedingsAndMedications/UI/MedicalSchedule/MedicalSchedule';
import type { Enrollment, MembershipItem } from '@spike/membership-model';

export enum wizardBooking {
    PetParent = 'petParent',
    Service = 'service',
    Staff = 'staff',
    Deposit = 'deposit',
    Notes = 'notes'
}

export enum itemTypes {
    addOn = 'add-on',
    product = 'product',
    custom = 'custom',
    service = 'service'
}

export enum versionPetAndServices {
    add = 'add',
    edit = 'edit',
    another = 'another'
}

export interface PetServiceWrapper extends PetService {
    enrollment?: Enrollment;
    membershipItem?: MembershipItem;
    duration?: Duration;
    beforePrice?: number;
    discount?: Discount;
    room?: Room;
    roomUnitId?: number;
    feedings?: Array<Feeding>;
    medications?: Array<Medication>;
    serviceItems?: Array<ServiceItem>;
    vehicle?: Vehicle;
    address?: string;
    instructions?: string;
    latePickupFee?: LatePickupFee;
    variableDuration?: string;
    bookingType?: SessionBookingType;
    maxPets?: number;
    inHomeService?: boolean;
    serviceSessionId?: number | null;
    notes?: string;
    selectedOffer?: BoardingOffer;
    saveCustomPrice?: boolean;
    customPriceId?: number;
    useSavedCustomPrice?: boolean;
    /** Unit amount to persist when it differs from the booking line price (e.g. daycare hourly). */
    customPriceAmount?: number;
}

export interface PetServiceTotal {
    price: number;
    durationInMinutes: number;
    duration?: Duration;
}

export interface Discount {
    type: 'none' | 'amount' | 'percentage';
    value: number;
}

export interface ItemData {
    discount: Discount;
    quantity?: number;
    price?: number;
    total?: number;
    taxId?: number;
    flow?: 'grooming' | 'dental' | 'daycare' | 'boarding' | 'transportation' | 'training' | 'walking';
    type?: 'pickup' | 'dropoff';
}

export interface DataVaraible {
    service?: PetServiceWrapper;
    staffId?: number;
    date: Moment;
    dateEnd?: Moment;
    type?: itemTypes;
    product?: Product;
    addOn?: AddOnService;
    customDescription?: string;
    daycare?: DaycareData;
    boarding?: BoardingData;
    itemData?: ItemData;
}

export interface AppointmentMultiple {
    clientId: number;
    petId: number;
    createdByStaffId: number;
    notes?: string;
    variableData: Array<DataVaraible>;
}

export interface DaycareData {
    daycare?: Daycare;
    feedings?: Array<Feeding>;
    medications?: Array<Medication>;
}

export interface BoardingData {
    boarding?: Boarding;
    feedings?: Array<Feeding>;
    medications?: Array<Medication>;
}

export interface AppointmentActions {
    service?: PetServiceWrapper;
    staffId?: number;
    date?: Moment;
    petId?: number;
    discount?: Discount;
    product?: Product;
    addOn?: AddOnService;
    custom?: string;
    type?: itemTypes;
    itemData?: ItemData;
    dateEnd?: Moment;
}

export interface AddOnDate {
    id?: number;
    date: string;
}

interface DosageQuantity {
    quantity?: number;
    unit?: number;
}

export type FeedingScheduleType = 'feedingMoment' | 'specificTime';

export interface Feeding {
    taskIds?: Array<number>;
    momentTypes: Array<number>;
    specificMomentTimes?: Record<number, string>;
    feedingScheduleType?: FeedingScheduleType;
    momentTypeToTaskIdMap?: Map<number, number[]>;
    foodProvisionType: number;
    foodType?: number;
    quantity: DosageQuantity;
    brand?: string;
    instructions?: string;
}

export interface Relief {
    momentTypes: Array<number>;
    notes?: string;
}

export interface Hydration {
    time: string;
    notes?: string;
}

export interface Medication {
    taskIds?: Array<number>;
    scheduleToTaskIdMap?: Map<string, number[]>;
    name: string;
    methodType?: number;
    dosage: DosageQuantity;
    instructions?: string;
    schedule: Array<string>;
    specificSchedule: Array<DaySchedule>;
}

export interface Daycare {
    from: Moment;
    to: Moment;
    service: DaycareServiceWrapper | undefined;
    room: Room | undefined;
    roomUnitId: number | undefined;
    price: number;
    priceForHour: number;
    discount: ValueTypeSelectorValue;
    enrollment?: Enrollment;
    membershipItem?: MembershipItem;
    selectedDates?: Array<Moment>;
    saveCustomPrice?: boolean;
    customPriceId?: number;
    useSavedCustomPrice?: boolean;
}

export interface Boarding {
    from: Moment;
    to: Moment;
    service: BoardingServiceWrapper | undefined;
    room: Room | undefined;
    roomUnitId: number | undefined;
    price: number;
    notes?: string;
    enrollment?: Enrollment;
    saveCustomPrice?: boolean;
    customPriceId?: number;
    useSavedCustomPrice?: boolean;
}

export interface TransportationData {
    price: number;
    discount: ValueTypeSelectorValue | undefined;
    time: {
        from: Moment;
        to: Moment;
    };
    address: string;
    instructions: string;
    saveCustomPrice?: boolean;
    customPriceId?: number;
    useSavedCustomPrice?: boolean;
}

export interface Transportation {
    transportType: TransportationType;
    service: TransportationService | undefined;
    selectedStaff: Staff | undefined;
    vehicle: Vehicle | undefined;
    pickup: TransportationData | undefined;
    dropOff: TransportationData | undefined;
    enrollment?: Enrollment;
    membershipItem?: MembershipItem;
    selectedDates?: Array<Moment>;
}

export interface Training {
    from: Moment;
    to: Moment;
    service: TrainingService | undefined;
    price: number;
    discount: ValueTypeSelectorValue;
    staffId: number | undefined;
    address?: string;
    addressOverride?: SessionAddressOverride;
    instructions?: string;
    serviceSessionId?: number | null;
    enrollment?: Enrollment;
    saveCustomPrice?: boolean;
    customPriceId?: number;
    useSavedCustomPrice?: boolean;
    membershipItem?: MembershipItem;
    selectedDates?: Array<Moment>;
    // Sessions picked across several selected dates, one appointment each. A selected date with
    // none books `from`'s time and `staffId` with no session link.
    sessionSlots?: Array<SessionSlot>;
}

export interface Walking {
    from: Moment;
    to: Moment;
    service: WalkingService | undefined;
    price: number;
    discount: ValueTypeSelectorValue;
    staffId: number | undefined;
    address?: string;
    addressOverride?: SessionAddressOverride;
    instructions?: string;
    serviceSessionId?: number | null;
    enrollment?: Enrollment;
    saveCustomPrice?: boolean;
    customPriceId?: number;
    useSavedCustomPrice?: boolean;
    membershipItem?: MembershipItem;
    selectedDates?: Array<Moment>;
    // Sessions picked across several selected dates, one appointment each. A selected date with
    // none books `from`'s time and `staffId` instead.
    sessionSlots?: Array<SessionSlot>;
}

export interface SessionSlot {
    from: Moment;
    staffId: number;
    serviceSessionId: number;
}

export interface SessionAddressOverride {
    latitude: number;
    longitude: number;
    zipcode?: string;
}

export enum OrderizableTypes {
    APPOINTMENT = 'appointment',
    ADDON_SERVICE = 'addon_service',
    PRODUCT = 'product',
    CUSTOM = 'custom'
}
